/**
 * `Intl` formatters, built once per (locale, options) instead of once per call.
 *
 * Constructing an `Intl.DateTimeFormat` is one of the most expensive things the
 * platform offers: it loads the locale's data and, for `fa-IR-u-ca-persian`, a
 * whole calendar implementation. Formatting with one that already exists is
 * cheap by comparison.
 *
 * The panel's tables read hundreds of rows, and every row formats a date, two
 * times and an amount — so a single re-render was building thousands of
 * formatters and throwing them all away. That is main-thread work measured in
 * whole frames: the table felt slow to type over, and the rest of the page went
 * down with it because there is only one thread to go round.
 *
 * Every formatter is immutable and fully described by its key, so the cache is
 * a pure memo — the same key always answers with an identically-behaving
 * object. It is bounded by the number of call sites, times two locales.
 */
const dateTimeFormats = new Map<string, Intl.DateTimeFormat>();
const numberFormats = new Map<string, Intl.NumberFormat>();

export function dateTimeFormat(
  locale: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  const cached = dateTimeFormats.get(key);

  if (cached !== undefined) {
    return cached;
  }

  const created = new Intl.DateTimeFormat(locale, options);
  dateTimeFormats.set(key, created);

  return created;
}

export function numberFormat(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  const cached = numberFormats.get(key);

  if (cached !== undefined) {
    return cached;
  }

  const created = new Intl.NumberFormat(locale, options);
  numberFormats.set(key, created);

  return created;
}
